import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { ArrowLeft, BadgeCheck, Coins, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { decideApproval, getApprovalsCentre, setManualApproval } from "@/lib/agents.functions";
import { Button } from "@/components/ui/button";
import { ukDateTime } from "@/lib/format";

export const Route = createFileRoute("/_authenticated/owner/approvals")({
  head: () => ({
    meta: [
      { title: "Approvals — Shields" },
      {
        name: "description",
        content: "Approve or reject Shields Credit grants and payouts before any automation acts on them.",
      },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: ApprovalsPage,
});

type Row = Record<string, any>;

const FILTERS = [
  { key: "all", label: "Everything" },
  { key: "plans", label: "Agent plans" },
  { key: "credits", label: "Credit grants" },
  { key: "payouts", label: "Payouts" },
] as const;

function matches(row: Row, filter: string) {
  const action = String(row["action"] ?? "");
  if (filter === "plans") return action.startsWith("business_proposal:");
  if (filter === "credits") return action.includes("credit");
  if (filter === "payouts") return action.includes("payout");
  return true;
}

function money(row: Row) {
  const change = (row["proposed_change"] ?? {}) as Record<string, any>;
  if (typeof change["credits"] === "number") return `${change["credits"]} SC`;
  if (typeof change["amount_pence"] === "number") return `£${(change["amount_pence"] / 100).toFixed(2)}`;
  return null;
}

function ApprovalsPage() {
  const queryClient = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState<string>("all");
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["owner-approvals"],
    queryFn: () => getApprovalsCentre(),
    retry: false,
  });

  async function act(run: () => Promise<any>, success: string) {
    setBusy(true);
    try {
      const res = await run();
      if (res && res.ok === false) toast.error(res.error ?? "That didn't work");
      else toast.success(res?.applied ? `${success} — ${res.applied}` : success);
      await queryClient.invalidateQueries({ queryKey: ["owner-approvals"] });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "That didn't work");
    } finally {
      setBusy(false);
    }
  }

  if (isLoading) {
    return (
      <div className="mx-auto max-w-4xl space-y-4 px-4 py-8">
        {[0, 1, 2].map((i) => (
          <div key={i} className="h-28 animate-pulse rounded-2xl bg-muted" />
        ))}
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="mx-auto max-w-4xl px-4 py-16 text-center">
        <p className="text-lg font-semibold text-foreground">We couldn't load your approvals.</p>
        <Button className="mt-4" onClick={() => void refetch()}>
          Try again
        </Button>
      </div>
    );
  }

  const pending = (data.pending as Row[]).filter((r) => matches(r, filter));
  const decided = (data.decided as Row[]).filter((r) => matches(r, filter));

  return (
    <div className="mx-auto max-w-4xl space-y-8 px-4 py-8">
      <Link to="/owner" className="text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft className="inline size-4" /> Control Room
      </Link>

      <header className="space-y-2">
        <h1 className="text-2xl font-semibold text-foreground">Approvals</h1>
        <p className="text-sm text-muted-foreground">
          Review work prepared by agents, credit grants, and payouts. An agent plan is a proposal; approving it records internal work, and connected services are required for external actions. Approving a credit grant adds the credits straight away.
        </p>
      </header>

      <section className="space-y-3 rounded-2xl border border-border bg-card p-5 shadow-sm">
        <p className="flex items-center gap-2 text-sm font-semibold text-foreground">
          <ShieldCheck className="size-4" /> Ask me first
        </p>
        {(
          [
            { key: "credit_grants", label: "Monthly membership credit grants", icon: Coins },
            { key: "payouts", label: "Partner payouts", icon: BadgeCheck },
          ] as const
        ).map(({ key, label, icon: Icon }) => {
          const on = Boolean((data.manual as Record<string, boolean>)[key]);
          return (
            <div key={key} className="flex flex-wrap items-center justify-between gap-3">
              <p className="flex items-center gap-2 text-sm text-foreground">
                <Icon className="size-4 text-muted-foreground" /> {label}
              </p>
              <Button
                size="sm"
                variant={on ? "default" : "outline"}
                disabled={busy}
                onClick={() =>
                  void act(
                    () => setManualApproval({ data: { department: key, required: !on } }),
                    on ? "Now handled automatically" : "Will wait for your approval",
                  )
                }
              >
                {on ? "Waiting for you" : "Automatic"}
              </Button>
            </div>
          );
        })}
      </section>

      <div className="flex flex-wrap gap-2">
        {FILTERS.map((f) => (
          <Button
            key={f.key}
            size="sm"
            variant={filter === f.key ? "default" : "outline"}
            onClick={() => setFilter(f.key)}
          >
            {f.label}
          </Button>
        ))}
      </div>

      <section className="space-y-3">
        <h2 className="text-lg font-semibold text-foreground">Waiting for your decision</h2>
        {pending.length === 0 ? (
          <p className="rounded-2xl border border-border bg-card p-5 text-sm text-muted-foreground">
            Nothing needs a decision right now.
          </p>
        ) : (
          pending.map((a) => (
            <div key={a["id"]} className="rounded-2xl border border-border bg-card p-5 shadow-sm">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <p className="font-semibold text-foreground">{a["proposed_change"]?.["title"] ?? a["action"]}</p>
                  <p className="mt-1 text-sm text-foreground">{a["reason"]}</p>
                  {String(a["action"]).startsWith("business_proposal:") ? <p className="mt-2 text-xs font-medium text-[#8b651e]">Prepared plan · {a["proposed_change"]?.["execution"]?.["requiresConnection"] ? "External provider connection needed before execution." : "Approval records internal work; no external action is claimed."}</p> : null}
                  <p className="mt-1 text-xs text-muted-foreground">
                    {a["agent_key"]} · {a["risk_level"]} risk · {ukDateTime(a["created_at"])}
                  </p>
                </div>
                {money(a) ? (
                  <span className="rounded-full bg-primary/10 px-3 py-1 text-sm font-semibold text-primary">
                    {money(a)}
                  </span>
                ) : null}
              </div>
              <div className="mt-3 flex gap-2">
                <Button
                  size="sm"
                  disabled={busy}
                  onClick={() =>
                    void act(
                      () => decideApproval({ data: { approvalId: a["id"], decision: "approved" } }),
                      "Approved",
                    )
                  }
                >
                  {String(a["action"]).startsWith("business_proposal:") ? "Approve plan" : "Approve"}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={busy}
                  onClick={() =>
                    void act(
                      () => decideApproval({ data: { approvalId: a["id"], decision: "rejected" } }),
                      "Rejected",
                    )
                  }
                >
                  Reject
                </Button>
              </div>
            </div>
          ))
        )}
      </section>

      <section className="space-y-3">
        <h2 className="text-lg font-semibold text-foreground">Recent decisions</h2>
        {decided.length === 0 ? (
          <p className="rounded-2xl border border-border bg-card p-5 text-sm text-muted-foreground">
            No decisions recorded yet.
          </p>
        ) : (
          <ul className="divide-y divide-border overflow-hidden rounded-2xl border border-border bg-card">
            {decided.map((a) => (
              <li key={a["id"]} className="p-4">
                <p className="text-sm font-medium text-foreground">
                  {a["action"]}{" "}
                  <span
                    className={
                      a["status"] === "approved"
                        ? "text-xs text-success"
                        : "text-xs text-muted-foreground"
                    }
                  >
                    ({a["status"]})
                  </span>
                </p>
                <p className="mt-1 text-sm text-foreground">{a["reason"]}</p>
                <p className="mt-1 text-xs text-muted-foreground">
                  {a["decided_at"] ? ukDateTime(a["decided_at"]) : ""}
                </p>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
